import { ErrorCode } from '@trulyinbox/errors';
import { ApiError } from '@/lib/api';
import { getErrorMessage } from '@/lib/errors';
import { formatDate, toDate } from '@/lib/date';
import { PAUSE_DURATION_MONTHS } from '@/lib/billing';
import type {
  LifecycleAction,
  PauseDurationMonths,
  ScheduledChangeAction,
  Subscription,
  SubscriptionScheduledChange,
} from '@/lib/billing';

/**
 * Non-component domain logic for the cancel / pause / resume / revoke flows,
 * kept out of the `.tsx` so the cards stay Vite Fast Refresh boundaries and each
 * rule is unit-testable without rendering.
 *
 * The single most important thing encoded here is that the actions do NOT behave
 * alike:
 *
 *   - **resume** is applied at Paddle `immediately`, so `subscription.resumed`
 *     lands within seconds and the local status flips to ACTIVE while the user
 *     watches. Polling for that flip is meaningful.
 *   - **cancel** and **pause** are applied `next_billing_period`. Paddle records
 *     a scheduled change and emits `subscription.canceled` / `subscription.paused`
 *     only when the period actually ends — days or weeks later. The local status
 *     stays ACTIVE the whole time, so polling for a status flip would spin for
 *     the full deadline and then report a false timeout on a request that
 *     succeeded perfectly. They resolve to `scheduled` instead.
 *   - **revoke-scheduled-change** is applied immediately but flips no status at
 *     all: `subscription.updated` lands within seconds and clears the pending
 *     change. It is polled — on `scheduledChange` going null, not on a status.
 *
 * NOTE — this comment previously asserted that the API "deliberately surfaces no
 * `scheduled_change` field". That is no longer true, and its being true was the
 * bug: with the pending change invisible, a user who paused their plan saw a
 * green "Active" chip forever and `availableLifecycleActions` kept offering a
 * Pause that could now only 422. Both reads (`GET /billing/subscription` and
 * `GET /billing/summary`) now carry `scheduledChange`, and every rule below
 * gates on it.
 *
 * `isImmediate` is the polled/not-polled distinction, and every caller branches
 * on it rather than re-deriving it.
 */

/** `true` when the action's effect (and its webhook) lands right away. */
export function isImmediate(action: LifecycleAction): boolean {
  return action === 'resume' || action === 'revoke-scheduled-change';
}

/**
 * The status the subscription reaches once `action` has been applied, or `null`
 * for an action that changes no status. `revoke-scheduled-change` is the only
 * `null`: it clears the pending change and leaves the subscription exactly as it
 * was, which is why `useSubscription` needs `pollUntilNoScheduledChange` — a
 * status target cannot express it.
 */
export const LIFECYCLE_TARGET_STATUS: Record<
  LifecycleAction,
  Subscription['status'] | null
> = {
  cancel: 'CANCELED',
  pause: 'PAUSED',
  resume: 'ACTIVE',
  'revoke-scheduled-change': null,
};

/**
 * Has `action` visibly landed on the polled subscription? The observable signal
 * differs by action — a status flip for cancel/pause/resume, `scheduledChange`
 * clearing for the revoke — so the poll target and this predicate must stay in
 * lockstep. Keeping both derivations here is what guarantees they do.
 */
export function lifecycleActionArrived(
  action: LifecycleAction,
  subscription: Subscription | undefined,
): boolean {
  if (!subscription) return false;
  if (action === 'revoke-scheduled-change') return subscription.scheduledChange == null;
  return subscription.status === LIFECYCLE_TARGET_STATUS[action];
}

export type LifecycleCopy = {
  /** Button label on the card. */
  trigger: string;
  /** Confirm-dialog title. */
  title: string;
  /** Confirm-dialog body. `periodEnd` is pre-formatted, or null when unknown. */
  body: (periodEnd: string | null) => string;
  /** Confirm-button label. */
  confirm: string;
  /**
   * Dismiss-button label — "back out without doing this". It is per-action
   * because the generic "Keep subscription" reads exactly BACKWARDS on the
   * revoke dialog, where backing out means leaving the pending cancel/pause in
   * place.
   */
  dismiss: string;
  /** Destructive styling on the confirm button. */
  destructive: boolean;
  /**
   * Terminal copy once the action has visibly landed (the `done` phase). Only
   * the immediate actions reach it — cancel/pause resolve to `scheduled` — but
   * every entry carries an accurate string rather than a placeholder, so a
   * future change to what gets polled can't surface a wrong sentence.
   */
  done: string;
};

export const LIFECYCLE_COPY: Record<LifecycleAction, LifecycleCopy> = {
  cancel: {
    trigger: 'Cancel subscription',
    title: 'Cancel your subscription?',
    // The customer keeps everything they already paid for — cancelling does NOT
    // cut access short — and there is no extra grace after that. Naming the date
    // is the whole point of this copy; without it "cancel" reads as "immediately".
    body: (periodEnd) =>
      periodEnd
        ? `Your plan stays active until ${periodEnd}, the end of the period you've already paid for. After that your organization moves to the Free plan and warmup limits drop to the Free tier. You can resubscribe at any time.`
        : "Your plan stays active until the end of the period you've already paid for. After that your organization moves to the Free plan and warmup limits drop to the Free tier. You can resubscribe at any time.",
    confirm: 'Cancel subscription',
    dismiss: 'Keep subscription',
    destructive: true,
    done: 'Your subscription has been cancelled.',
  },
  pause: {
    trigger: 'Pause subscription',
    title: 'Pause your subscription?',
    // Three consequences, all of which users get blindsided by otherwise. The
    // manual re-enable is the one that generates support tickets: resuming the
    // subscription does NOT restart warmup, because paused accounts land in
    // WARMUP_PAUSED and the auto-resume sweep only targets SUSPENDED_* states.
    // Deliberately NO downgrade deadline — a pause is indefinite.
    body: (periodEnd) =>
      `${
        periodEnd
          ? `Billing stops at ${periodEnd}, the end of your current period.`
          : 'Billing stops at the end of your current period.'
      } Warmup then stops on every mailbox in your organization, and stays stopped: when you resume, you'll need to re-enable each mailbox yourself. Your data is kept, and the pause lasts until you resume.`,
    confirm: 'Pause subscription',
    dismiss: 'Keep subscription',
    destructive: true,
    done: 'Your subscription is paused.',
  },
  resume: {
    trigger: 'Resume subscription',
    title: 'Resume your subscription?',
    // Immediate, and the one action whose result the user watches land.
    body: () =>
      "Billing restarts now and your plan's limits are restored. Warmup does not restart on its own. Re-enable each mailbox you want warming again.",
    confirm: 'Resume subscription',
    dismiss: 'Not now',
    destructive: false,
    done: 'Your subscription is active again. Re-enable each mailbox you want warming.',
  },
  'revoke-scheduled-change': {
    // The undo for a pending cancel/pause. Phrased as what the user KEEPS, not
    // as "revoke" — nobody thinks of their own click as a scheduled change.
    trigger: 'Keep my subscription',
    title: 'Keep your subscription?',
    // Deliberately action-agnostic (it reads the same for a pending cancel and a
    // pending pause): the card renders the pending line naming the action
    // directly above this button, so repeating it here buys nothing and would
    // need the action threaded through the whole copy type for one string.
    body: (periodEnd) =>
      periodEnd
        ? `The change scheduled for ${periodEnd} will be called off and your subscription carries on as normal: same plan, same price, same billing date. You can schedule it again at any time.`
        : 'The scheduled change will be called off and your subscription carries on as normal: same plan, same price, same billing date. You can schedule it again at any time.',
    confirm: 'Keep my subscription',
    // Backing out here LEAVES the pending change booked — say so.
    dismiss: 'Leave it scheduled',
    destructive: false,
    done: 'Nothing is scheduled to change. Your subscription carries on as normal.',
  },
};

/** Post-success copy. Cancel/pause are scheduled; resume and revoke are done. */
export function lifecycleSuccessMessage(
  action: LifecycleAction,
  periodEnd: string | null,
): string {
  switch (action) {
    case 'cancel':
      return periodEnd
        ? `Subscription cancelled. Your plan stays active until ${periodEnd}.`
        : 'Subscription cancelled. Your plan stays active until the end of the current period.';
    case 'pause':
      return periodEnd
        ? `Subscription will pause on ${periodEnd}.`
        : 'Subscription will pause at the end of the current period.';
    case 'resume':
      return 'Subscription resumed.';
    case 'revoke-scheduled-change':
      return 'Scheduled change called off. Your subscription continues as normal.';
  }
}

/**
 * User-facing copy for a failed lifecycle request, branched on the 6-digit
 * `errorCode` — never on message text (the data-layer rule).
 *
 * All six 422s are genuinely reachable without a bug: `GET /billing/subscription`
 * is cached for 60s, so a second tab, a stale card, or a webhook that landed
 * moments ago can all leave the UI offering an action the backend now rejects.
 * `ChangeAlreadyScheduled` and `NoScheduledChange` are the two halves of that
 * race around a pending change — one arrives when the mirror is staler than the
 * booking, the other when it is fresher. `PauseAnchorUnknown` needs no race at
 * all: a subscription with no period end (a first cycle whose renewal webhook
 * hasn't landed) offers the duration picker like any other, and only the submit
 * can discover there is nothing to measure the months from. Anything unmapped
 * falls back to the server's message.
 */
export function lifecycleErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.errorCode) {
      case ErrorCode.Billing.Lifecycle.NoPaddleSubscription:
        return 'This organization has no active paid subscription to change.';
      case ErrorCode.Billing.Lifecycle.NotActive:
        return 'Your subscription is no longer active, so it can’t be cancelled or paused. Refresh to see its current state.';
      case ErrorCode.Billing.Lifecycle.NotPaused:
        return 'Your subscription isn’t paused, so there’s nothing to resume. Refresh to see its current state.';
      case ErrorCode.Billing.Lifecycle.ChangeAlreadyScheduled:
        return 'A change to your subscription is already scheduled, and only one can be pending at a time. Refresh to see it. You can call it off first if you want to schedule something else.';
      case ErrorCode.Billing.Lifecycle.NoScheduledChange:
        return 'There’s no scheduled change left to call off. It may already have been revoked or taken effect. Refresh to see your subscription’s current state.';
      // Only a FIXED-LENGTH pause can raise this: with no period end on the
      // subscription there is nothing to count the months from, so the backend
      // refuses rather than booking a pause whose resume date it can't answer.
      // An indefinite pause needs no anchor, which is why the copy points there.
      case ErrorCode.Billing.Lifecycle.PauseAnchorUnknown:
        return 'We can’t work out when a fixed-length pause would end, because your subscription has no renewal date yet. Choose “Until I resume” instead. You can resume whenever you like.';
      case ErrorCode.Billing.Lifecycle.RequestFailed:
        return 'We couldn’t reach the payment provider. Nothing was changed. Please try again in a moment.';
    }
  }
  return getErrorMessage(err, 'Could not update your subscription.');
}

/**
 * Which lifecycle actions to offer for a subscription, in display order.
 *
 * Mirrors the backend's `runLifecycleAction` preconditions so the card never
 * shows a button whose only outcome is a 422:
 *   - all actions require a Paddle-backed subscription (free / trial /
 *     manually-assigned orgs have none);
 *   - a PENDING SCHEDULED CHANGE outranks everything else. Paddle allows exactly
 *     one at a time, so cancel/pause/resume all throw `041105
 *     ChangeAlreadyScheduled` while one is booked (the backend guard is on
 *     `scheduledChangeAction != null`, irrespective of status — this mirrors it
 *     exactly). Offering them anyway is what produced the misleading generic 502
 *     on a second click, so the only action here is the revoke;
 *   - otherwise cancel/pause require ACTIVE and resume requires PAUSED.
 */
export function availableLifecycleActions(
  subscription: Subscription | undefined,
): LifecycleAction[] {
  if (!subscription?.paddleSubscriptionId) return [];
  if (subscription.scheduledChange != null) return ['revoke-scheduled-change'];
  if (subscription.status === 'ACTIVE') return ['pause', 'cancel'];
  if (subscription.status === 'PAUSED') return ['resume'];
  return [];
}

/**
 * An ISO instant formatted for copy, or `null` when it is absent or unparseable.
 * Module-private, and every date this file renders goes through it, so the
 * "degrade rather than print `Invalid Date` or invent one" rule is enforced in
 * exactly one place instead of once per caller.
 */
function formatIsoDate(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const date = toDate(raw);
  return Number.isNaN(date.getTime()) ? null : formatDate(date);
}

/** The current period end, formatted for copy, or null when not yet known. */
export function formatPeriodEnd(subscription: Subscription | undefined): string | null {
  return formatIsoDate(subscription?.currentPeriodEnd ?? subscription?.expiresAt ?? null);
}

/**
 * The pause-length picker's options, in display order, built FROM the wire-level
 * `PAUSE_DURATION_MONTHS` so the list the user sees and the list the server
 * accepts cannot drift.
 *
 * "Until I resume" is last but is the PRESELECTED default (see
 * `useSubscriptionLifecycle`): it is exactly what a pause did before this picker
 * existed, so anyone who ignores the control gets the behaviour they had.
 */
export const PAUSE_INDEFINITE_VALUE = 'indefinite';

export const PAUSE_DURATION_OPTIONS: { value: string; label: string }[] = [
  ...PAUSE_DURATION_MONTHS.map((months) => ({
    value: String(months),
    // Pluralized, not `${months} months`: the offered list is data, and once it
    // includes a 1 the hardcoded plural renders "1 months" in the dialog. Same
    // inline `=== 1 ? '' : 's'` idiom the rest of the app uses.
    label: `${months} month${months === 1 ? '' : 's'}`,
  })),
  { value: PAUSE_INDEFINITE_VALUE, label: 'Until I resume' },
];

/**
 * A picker value back to the wire value: a member of `PAUSE_DURATION_MONTHS`, or
 * `null` for the indefinite pause. Matching against the array rather than
 * `Number(value)` is what keeps the return type the narrow union — an unknown
 * string degrades to the indefinite default instead of being cast into a length
 * the server would reject.
 */
export function parsePauseDuration(value: string): PauseDurationMonths | null {
  return PAUSE_DURATION_MONTHS.find((months) => String(months) === value) ?? null;
}

/**
 * Third-person verb for the pending-change line. Module-private: the one place
 * this vocabulary is rendered is `scheduledChangeLine` below, and the banner's
 * copy needs a different grammatical form ("scheduled to be cancelled"), so
 * exporting this would invite a false merge of two genuinely different sentences.
 */
const SCHEDULED_CHANGE_VERB: Record<ScheduledChangeAction, string> = {
  cancel: 'Cancels',
  pause: 'Pauses',
  resume: 'Resumes',
};

/**
 * The what's-coming line — "Pauses on Aug 30, 2026", "Pauses on Aug 30, 2026 ·
 * resumes Nov 30, 2026", "Resumes on Nov 30, 2026" — shown under the plan name
 * on BOTH the billing card and the sidebar plan card, so the two can't drift.
 * `null` when there is nothing coming, which is also how each caller decides
 * whether to render the line at all.
 *
 * This is deliberately a SEPARATE line rather than a change to the status chip:
 * the plan is still fully active and paid for until the effective date, so
 * relabelling "Active" as "Pausing" would misstate the current state. The chip
 * keeps the truth; this line carries what's coming.
 *
 * It covers THREE states, not two, because a fixed-length pause is visible on
 * either side of its effective date and the second side has no `scheduledChange`
 * at all:
 *   - a pending cancel/pause/resume → the verb plus its effective date;
 *   - a pending pause with a fixed length → the same, plus when it comes back;
 *   - a pause that has ALREADY landed → `scheduledChange` is back to `null` and
 *     `scheduledResumeAt` is the only thing left that knows a resume is coming,
 *     so it gets its own branch rather than being folded into the verb map.
 *
 * Degrades to "at period end" when Paddle sent no `effective_at`, and drops the
 * resume clause when that date is absent or unparseable — never a bare verb,
 * never an invented date.
 */
export function scheduledChangeLine(
  change: SubscriptionScheduledChange | null | undefined,
  scheduledResumeAt?: string | null,
): string | null {
  const resumesOn = formatIsoDate(scheduledResumeAt);

  if (!change) {
    // The landed pause. The server suppresses `scheduledResumeAt` unless a pause
    // is booked or in effect, so with no pending change its presence alone
    // identifies this state — no `status` argument needed to disambiguate.
    // Reuses the verb map rather than re-typing "Resumes" so the two sentences
    // that render that word can never disagree.
    return resumesOn ? `${SCHEDULED_CHANGE_VERB.resume} on ${resumesOn}` : null;
  }

  const verb = SCHEDULED_CHANGE_VERB[change.action];
  const effectiveOn = formatIsoDate(change.effectiveAt);
  const head = effectiveOn ? `${verb} on ${effectiveOn}` : `${verb} at period end`;

  // Only a pending PAUSE has a return date to add: on a cancel there is nothing
  // to come back to, and on a pending resume `effectiveAt` already IS the resume
  // date. The server sends no `scheduledResumeAt` for either, so this gate is
  // belt-and-braces against a line that would read as nonsense.
  return change.action === 'pause' && resumesOn
    ? `${head} · resumes ${resumesOn}`
    : head;
}

/**
 * The inline CTA that undoes a pending change, keyed by what is pending, or
 * `null` when there is nothing worth undoing.
 *
 * `resume` is `null` on purpose: a scheduled resume is a GOOD outcome, so its
 * line stays plain text. Typed `string | null` rather than a `Partial<Record<…>>`
 * so a fifth `ScheduledChangeAction` fails the build here instead of silently
 * rendering no CTA.
 *
 * These are deliberately NOT `LIFECYCLE_COPY['revoke-scheduled-change'].trigger`
 * ("Keep my subscription"). That label sits on a standalone button under a
 * "Manage subscription" heading, where generic phrasing is right because nothing
 * around it names the pending action. These sit INLINE at the end of "Cancels on
 * Aug 30" / "Pauses on Aug 30", where the sentence has already named it and the
 * CTA has to read as that sentence's direct undo. Merging the two would change a
 * rendered string on one surface or the other.
 */
const SCHEDULED_CHANGE_UNDO_LABEL: Record<ScheduledChangeAction, string | null> = {
  cancel: 'Keep my plan',
  pause: 'Don’t pause',
  resume: null,
};

export function scheduledChangeUndoLabel(
  change: SubscriptionScheduledChange | null | undefined,
): string | null {
  return change ? SCHEDULED_CHANGE_UNDO_LABEL[change.action] : null;
}
